/**
 * Orchestra AI Platform - Environment Configuration
 * 
 * Centralized environment configuration with validation
 * 
 * @fileoverview Environment variables and feature flags
 */

/**
 * Environment configuration object
 * 
 * Note: This project uses react-scripts (Create React App), which uses process.env
 * For Vite projects, use import.meta.env instead
 */
const getEnvVar = (key, defaultValue = '') => {
  // Try process.env first (for react-scripts/CRA)
  if (typeof process !== 'undefined' && process.env) {
    return process.env[key] || defaultValue;
  }
  // Fallback to import.meta.env (for Vite)
  if (typeof import.meta !== 'undefined' && import.meta.env) {
    return import.meta.env[key] || defaultValue;
  }
  return defaultValue;
};

const getEnvBool = (key, defaultValue = false) => {
  const value = getEnvVar(key, '');
  return value === 'true' || defaultValue;
};

const getNodeEnv = () => {
  if (typeof process !== 'undefined' && process.env?.NODE_ENV) {
    return process.env.NODE_ENV;
  }
  if (typeof import.meta !== 'undefined' && import.meta.env?.NODE_ENV) {
    return import.meta.env.NODE_ENV;
  }
  return 'development';
};

export const config = {
  supabase: {
    url: getEnvVar('REACT_APP_SUPABASE_URL') || getEnvVar('VITE_SUPABASE_URL') || '',
    anonKey: getEnvVar('REACT_APP_SUPABASE_ANON_KEY') || getEnvVar('VITE_SUPABASE_ANON_KEY') || '',
  },
  aiServers: {
    chinese: getEnvVar('REACT_APP_CHINESE_AI_SERVER_URL') || getEnvVar('VITE_CHINESE_AI_SERVER_URL') || 'https://a2achineseai-server.appsokay.net',
    english: getEnvVar('REACT_APP_ENGLISH_AI_SERVER_URL') || getEnvVar('VITE_ENGLISH_AI_SERVER_URL') || 'https://a2aenglishai-server.appsokay.net',
    maths: getEnvVar('REACT_APP_MATHS_AI_SERVER_URL') || getEnvVar('VITE_MATHS_AI_SERVER_URL') || 'https://a2amathsai-server.appsokay.net',
    myclass: getEnvVar('REACT_APP_MYCLASS_AI_SERVER_URL') || getEnvVar('VITE_MYCLASS_AI_SERVER_URL') || 'https://a2amyclassai-server.appsokay.net',
  },
  features: {
    collaboration: getEnvBool('REACT_APP_ENABLE_COLLABORATION') || getEnvBool('VITE_ENABLE_COLLABORATION'),
    realtime: getEnvBool('REACT_APP_ENABLE_REALTIME') || getEnvBool('VITE_ENABLE_REALTIME'),
    analytics: getEnvBool('REACT_APP_ENABLE_ANALYTICS') || getEnvBool('VITE_ENABLE_ANALYTICS'),
  },
  monitoring: {
    sentryDsn: getEnvVar('REACT_APP_SENTRY_DSN') || getEnvVar('VITE_SENTRY_DSN') || '',
    analyticsId: getEnvVar('REACT_APP_ANALYTICS_ID') || getEnvVar('VITE_ANALYTICS_ID') || '',
  },
  api: {
    baseUrl: getEnvVar('REACT_APP_API_BASE_URL') || getEnvVar('VITE_API_BASE_URL') || '',
  },
  isProduction: getNodeEnv() === 'production',
  isDevelopment: getNodeEnv() === 'development',
  isTest: getNodeEnv() === 'test',
};

/**
 * Validate required environment variables
 * @throws {Error} If required variables are missing
 */
export function validateEnvironment() {
  const required = [
    { key: 'REACT_APP_SUPABASE_URL or VITE_SUPABASE_URL', value: config.supabase.url },
    { key: 'REACT_APP_SUPABASE_ANON_KEY or VITE_SUPABASE_ANON_KEY', value: config.supabase.anonKey },
  ];

  const missing = required.filter(({ value }) => !value);

  if (missing.length > 0 && config.isProduction) {
    throw new Error(
      `Missing required environment variables: ${missing.map((m) => m.key).join(', ')}`
    );
  }

  if (missing.length > 0 && config.isDevelopment) {
    console.warn(
      `Warning: Missing environment variables: ${missing.map((m) => m.key).join(', ')}`
    );
  }
}

// Validate on import in production
if (config.isProduction) {
  validateEnvironment();
}

